/**
 * 认证上下文 - 集中管理认证状态和跨标签页同步
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { creditsKeys } from '@/hooks/api/useCredits';
import { creditsApi } from '@/services/api/credits';
import { useAppStore } from '@/stores/appStore';
import { applyUserShortcutPreferences } from '@/stores/canvasShortcutStore';
import { useOrgContextStore } from '@/stores/orgContextStore';
import { registerClearAuth } from '@/lib/auth-manager';
import { getAuthBroadcast, type AuthMessage } from '@/lib/broadcast-auth';
import { errorTracker } from '@/lib/error-tracking';
import { analyticsTracker } from '@/lib/analytics';
import type { User } from '@/types';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  login: (token: string, user: User) => Promise<void>;
  syncUser: (user: User) => void;
  logout: () => void;
  clearAuth: (reason?: string) => void;
  isClearing: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const { user, setUser, setCurrentProjectId } = useAppStore();
  const resetOrgContext = useOrgContextStore((s) => s.reset);
  const [isClearing, setIsClearing] = useState(false);

  /**
   * 登录：保存 token 和用户信息（异步确保完成）
   */
  const login = useCallback(
    async (token: string, userData: User) => {
      console.log('🔐 AuthContext: 执行登录');

      // 清除认证失效标记（如果存在）
      sessionStorage.removeItem('auth_clearing');

      localStorage.setItem('token', token);
      setUser(userData);
      applyUserShortcutPreferences(userData);
      errorTracker.setUserContext({ userId: userData.userId });
      analyticsTracker.identify(userData.userId);
      analyticsTracker.track('user_signed_in');

      // 等待一个微任务周期，确保状态更新和 localStorage 写入完成
      await new Promise((resolve) => setTimeout(resolve, 0));
      console.log('✅ AuthContext: 登录状态已同步');

      void queryClient.prefetchQuery({
        queryKey: creditsKeys.ratios(),
        queryFn: () => creditsApi.getRatios(),
      });

      // 广播登录事件到其他标签页
      const broadcast = getAuthBroadcast();
      broadcast.broadcastLogin(token, userData);
    },
    [queryClient, setUser]
  );

  /**
   * 同步用户信息：仅更新用户状态，不触发跨标签页同步
   * 用于页面刷新后恢复用户信息
   */
  const syncUser = useCallback(
    (userData: User) => {
      console.log('🔄 AuthContext: 同步用户信息');
      setUser(userData);
      applyUserShortcutPreferences(userData);
      errorTracker.setUserContext({ userId: userData.userId });
      analyticsTracker.identify(userData.userId);
    },
    [setUser]
  );

  /**
   * 退出登录：清除所有认证状态
   */
  const logout = useCallback(() => {
    console.log('🚪 AuthContext: 执行退出登录');
    analyticsTracker.track('user_signed_out');
    analyticsTracker.identify(null);
    localStorage.removeItem('token');
    setUser(null);
    applyUserShortcutPreferences(null);
    setCurrentProjectId(null); // 清除持久化的项目ID，避免新用户继承旧会话的项目状态
    resetOrgContext(); // 清除组织上下文（含 sessionStorage active_org_id）
    errorTracker.setUserContext(null);
    queryClient.clear(); // 清除所有 React Query 缓存

    // 广播退出登录事件到其他标签页
    const broadcast = getAuthBroadcast();
    broadcast.broadcastLogout();
  }, [setUser, setCurrentProjectId, resetOrgContext, queryClient]);

  /**
   * 清除认证状态（用于 403/401 处理）
   * 使用 sessionStorage 标记，确保会话期间只清除一次
   */
  const clearAuth = useCallback(
    (reason?: string) => {
      // 检查是否已在清除状态
      const isClearing = sessionStorage.getItem('auth_clearing');
      if (isClearing === 'true') {
        console.log('⚠️ AuthContext: 认证已失效，跳过重复清除');
        return;
      }

      // 设置标记，阻止后续请求和重复清除
      sessionStorage.setItem('auth_clearing', 'true');
      setIsClearing(true);

      console.log(`🔒 AuthContext: 清除认证状态 (原因: ${reason || '未知'})`);

      analyticsTracker.identify(null);
      localStorage.removeItem('token');
      setUser(null);
      applyUserShortcutPreferences(null);
      setCurrentProjectId(null); // 清除持久化的项目ID，避免新用户继承旧会话的项目状态
      resetOrgContext(); // 清除组织上下文
      errorTracker.setUserContext(null);
      queryClient.clear();

      // 跳转到登录页（统一在此处理，避免多处跳转）
      if (window.location.pathname !== '/login') {
        console.log('🔄 AuthContext: 跳转到登录页');
        window.location.href = '/login';
      }
    },
    [setUser, setCurrentProjectId, resetOrgContext, queryClient]
  );

  /**
   * 注册全局 clearAuth（供 axios 拦截器使用）
   */
  useEffect(() => {
    registerClearAuth(clearAuth);
  }, [clearAuth]);

  /** 会话恢复时已登录：预取词元倍率（立项等依赖长缓存单次拉取） */
  useEffect(() => {
    if (!user?.userId) return;
    void queryClient.prefetchQuery({
      queryKey: creditsKeys.ratios(),
      queryFn: () => creditsApi.getRatios(),
    });
  }, [user?.userId, queryClient]);

  /**
   * 跨标签页同步：BroadcastChannel 消息监听
   */
  useEffect(() => {
    const broadcast = getAuthBroadcast();

    const handleMessage = (message: AuthMessage) => {
      switch (message.type) {
        case 'LOGIN':
          // 其他标签页登录了，同步用户信息
          console.log('✅ AuthContext: 其他标签页已登录，同步状态');
          setUser(message.user);
          applyUserShortcutPreferences(message.user);
          queryClient.clear();
          // 如果当前在登录页，跳转到首页
          if (window.location.pathname === '/login') {
            window.location.href = '/';
          }
          break;

        case 'LOGOUT':
          // 其他标签页退出了，清除本地状态
          console.log('🚪 AuthContext: 其他标签页已退出，清除本地状态');
          setUser(null);
          applyUserShortcutPreferences(null);
          queryClient.clear();
          // 如果当前不在登录页，跳转到登录页
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          break;

        case 'PING':
          // 其他标签页询问登录状态，响应 PONG
          console.log('📡 AuthContext: 收到 PING，响应当前登录状态');
          broadcast.sendPong(user);
          break;

        case 'PONG':
          // 收到其他标签页的登录状态响应
          if (message.user) {
            console.log('✅ AuthContext: 收到 PONG，同步用户信息');
            setUser(message.user);
            applyUserShortcutPreferences(message.user);
          }
          break;
        default: {
          const _exhaustive: never = message;
          void _exhaustive;
        }
      }
    };

    const unsubscribe = broadcast.onMessage(handleMessage);
    return unsubscribe;
  }, [user, setUser, queryClient]);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!localStorage.getItem('token'),
    login,
    syncUser,
    logout,
    clearAuth,
    isClearing,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth Hook
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth 必须在 AuthProvider 内部使用');
  }
  return context;
}
